import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "@/api/base/private/privateBaseQuery";
import {
  type ApiEnvelope,
  type ApiEntitiesEnvelope,
  unwrapEntity,
  unwrapEntities,
} from "@/types/canonical/shared/envelope";

export type ChatType = "group" | "private" | (string & {});
export type ChatsOrderBy = "last_message" | "created_at" | (string & {});

export interface ChatLastMessageUser {
  name: string | null;
}

export interface ChatLastMessage {
  user: ChatLastMessageUser;
  message: string | null;
  created_at: string | null;
}

export interface ChatListItem {
  id: number;
  name: string;
  description: string | null;
  image: string | null;
  /** Hex without leading # per backend examples, e.g. "DBB818" */
  color: string | null;
  last_message: ChatLastMessage | null;
  participants_count: number | null;
}

export interface ChatsPaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

/**
 * List (pagination envelope) for /chats.
 * Backend usually wraps this as { status, status_code, entities }.
 */
export interface ChatsEntities {
  current_page: number;
  data: ChatListItem[];
  first_page_url?: string;
  from?: number;
  last_page: number;
  last_page_url?: string;
  links?: ChatsPaginationLink[];
  next_page_url?: string | null;
  path?: string;
  per_page?: number;
  prev_page_url?: string | null;
  to?: number;
  total?: number;
}

export interface GetChatsParams {
  page?: number;
  limit?: number;
  type?: ChatType;
  orderBy?: ChatsOrderBy;
}

/**
 * Chat messages
 *
 * GET /chat-messages?chat_id=...&page=...&limit=...&paginable=true
 */
export interface ChatMessageUser {
  id: number;
  firstname: string | null;
  lastname: string | null;
  photo: string | null;
  /** Hex without leading # per backend examples, e.g. "5E4E7A" */
  color: string | null;
  is_online?: boolean | null;
}

export interface ChatMessageItem {
  id: number;
  user_id: number | null;
  recipient_id: number | null;
  parent_id: number | null;
  action: string | null;
  message: string | null;
  media: unknown | null;
  is_service: number | boolean | null;
  created_at: string | null;
  user: ChatMessageUser | null;
}

export interface ChatMessagesEntities {
  current_page: number;
  data: ChatMessageItem[];
  first_page_url?: string;
  from?: number;
  last_page: number;
  last_page_url?: string;
  links?: ChatsPaginationLink[];
  next_page_url?: string | null;
  path?: string;
  per_page?: number;
  prev_page_url?: string | null;
  to?: number;
  total?: number;
}

export interface GetChatMessagesParams {
  chat_id: number;
  page?: number;
  limit?: number;
  paginable?: boolean;
}

export interface PostChatMessageBody {
  chat_id: number;
  recipient_id?: number | null;
  parent_id?: number | null;
  media_id?: number | null;
  message: string;
}

export const chatsApi = createApi({
  reducerPath: "chatsApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["ChatsList", "ChatMessages"],
  endpoints: (builder) => ({
    getChats: builder.query<ChatsEntities, GetChatsParams | void>({
      query: (params) => ({
        url: "/chats",
        method: "GET",
        params: {
          page: (params as GetChatsParams | undefined)?.page ?? 1,
          limit: (params as GetChatsParams | undefined)?.limit ?? 30,
          ...((params as GetChatsParams | undefined)?.type
            ? { type: (params as GetChatsParams).type }
            : {}),
          ...((params as GetChatsParams | undefined)?.orderBy
            ? { orderBy: (params as GetChatsParams).orderBy }
            : {}),
        },
      }),
      transformResponse: (
        response: ApiEntitiesEnvelope<ChatsEntities> | ChatsEntities
      ) => unwrapEntities<ChatsEntities>(response),
      providesTags: ["ChatsList"],
    }),

    getChatMessages: builder.query<ChatMessagesEntities, GetChatMessagesParams>({
      query: (params) => ({
        url: "/chat-messages",
        method: "GET",
        params: {
          chat_id: params.chat_id,
          page: params.page ?? 1,
          limit: params.limit ?? 25,
          paginable: params.paginable ?? true,
        },
      }),
      transformResponse: (
        response: ApiEntitiesEnvelope<ChatMessagesEntities> | ChatMessagesEntities
      ) => unwrapEntities<ChatMessagesEntities>(response),
      providesTags: (_result, _err, arg) => [{ type: "ChatMessages", id: arg.chat_id }],
    }),

    getChatMessageById: builder.query<ChatMessageItem, number>({
      query: (messageId) => ({
        url: `/chat-messages/${messageId}`,
        method: "GET",
      }),
      transformResponse: (
        response: ApiEnvelope<ChatMessageItem> | ChatMessageItem
      ) => unwrapEntity<ChatMessageItem>(response),
    }),

    postChatMessage: builder.mutation<ChatMessageItem, PostChatMessageBody>({
      query: (body) => ({
        url: "/chat-messages",
        method: "POST",
        body,
      }),
      transformResponse: (
        response: ApiEnvelope<ChatMessageItem> | ChatMessageItem
      ) => unwrapEntity<ChatMessageItem>(response),
      invalidatesTags: (_result, _err, arg) => [
        "ChatsList",
        { type: "ChatMessages", id: arg.chat_id },
      ],
    }),
  }),
});

export const {
  useGetChatsQuery,
  useGetChatMessagesQuery,
  useGetChatMessageByIdQuery,
  usePostChatMessageMutation,
} = chatsApi;